/**
 * ContentManagerDashboard - Multi-track workspace for Content Managers
 *
 * A hybrid approach dashboard that:
 * 1. Shows active work across categories at different maturity stages
 * 2. Provides AI-driven recommendations based on current state
 * 3. Organizes all tools by purpose in collapsible sections
 *
 * Content Managers work on multiple categories simultaneously - this is NOT a linear pipeline.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import { WelcomeModal } from '@/components/content-manager/WelcomeModal';
import { CategoryWorkspacePanel, CategoryWorkItem } from '@/components/content-manager/CategoryWorkspacePanel';
import { SmartRecommendations, Recommendation } from '@/components/content-manager/SmartRecommendations';
import { CapabilityToolkits } from '@/components/content-manager/CapabilityToolkits';
import { MarketIntelligencePanel, MarketIntelligenceData } from '@/components/content-manager/MarketIntelligencePanel';
import { AEOPerformanceHighlights, AEOHighlightsData } from '@/components/content-manager/AEOPerformanceHighlights';
import { HelpPanel } from '@/components/content-manager/ContextualHelp';
import { MySuggestionsWidget } from '@/components/home/MySuggestionsWidget';
import { GoodNewsWidget } from '@/components/content-manager/GoodNewsWidget';
import {
  Package,
  Sparkles,
  FileText,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  BarChart3,
  Target,
  CheckCircle2
} from 'lucide-react';

interface DashboardMetrics {
  totalProducts: number;
  enrichedProducts: number;
  enrichmentPercentage: number;
  aeoPages: number;
  aeoCategories: number;
  pendingReviews: number;
  pendingApprovals: number;
  recentArticles: number;
  lastFeedUpload: string | null;
  topCategories: Array<{ name: string; count: number; enriched: number }>;
}

export default function ContentManagerDashboard() {
  const navigate = useNavigate();
  const { selectedSite, loading: siteLoading } = useSite();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [categoryWorkItems, setCategoryWorkItems] = useState<CategoryWorkItem[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [recommendationsLoading, setRecommendationsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [marketIntelligence, setMarketIntelligence] = useState<MarketIntelligenceData | null>(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [aeoHighlights, setAeoHighlights] = useState<AEOHighlightsData | null>(null);
  const [aeoLoading, setAeoLoading] = useState(false);

  // Check if this is the first visit for this content manager
  useEffect(() => {
    const welcomeKey = `cm_welcome_shown_${user?.id}`;
    const hasSeenWelcome = localStorage.getItem(welcomeKey);
    if (!hasSeenWelcome && user?.role === 'site_content_manager') {
      setShowWelcome(true);
    }
  }, [user]);

  const handleWelcomeClose = () => {
    if (user?.id) {
      localStorage.setItem(`cm_welcome_shown_${user.id}`, 'true');
    }
    setShowWelcome(false);
  };

  useEffect(() => {
    if (selectedSite) {
      fetchDashboardData();
    }
  }, [selectedSite]);

  const fetchDashboardData = async (forceRefresh = false) => {
    if (!selectedSite) return;

    setLoading(true);
    setWorkspaceLoading(true);
    setRecommendationsLoading(true);
    setMarketLoading(true);
    setAeoLoading(true);
    setError(null);

    try {
      const siteId = selectedSite._id || selectedSite.id;
      const refreshParam = forceRefresh ? '?refresh=true' : '';

      // Fetch all data in parallel
      const [metricsRes, workspaceRes, recommendationsRes, marketRes, aeoRes] = await Promise.all([
        authFetch(`/api/content-manager-dashboard/site/${siteId}/metrics${refreshParam}`),
        authFetch(`/api/content-manager-dashboard/site/${siteId}/workspace${refreshParam}`),
        authFetch(`/api/content-manager-dashboard/site/${siteId}/recommendations${refreshParam}`),
        authFetch(`/api/content-manager-dashboard/site/${siteId}/market-intelligence${refreshParam}`),
        authFetch(`/api/content-manager-dashboard/site/${siteId}/aeo-highlights${refreshParam}`)
      ]);

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        if (data.success && data.metrics) {
          setMetrics(data.metrics);
        }
      }

      if (workspaceRes.ok) {
        const data = await workspaceRes.json();
        if (data.success && data.categories) {
          setCategoryWorkItems(data.categories);
        }
      } else {
        // Generate mock workspace data from metrics if endpoint doesn't exist yet
        generateWorkspaceFromMetrics();
      }

      if (recommendationsRes.ok) {
        const data = await recommendationsRes.json();
        if (data.success && data.recommendations) {
          setRecommendations(data.recommendations);
        }
      } else {
        // Generate recommendations from available data
        generateRecommendationsFromMetrics();
      }

      if (marketRes.ok) {
        const data = await marketRes.json();
        if (data.success && data.marketIntelligence) {
          setMarketIntelligence(data.marketIntelligence);
        }
      }

      if (aeoRes.ok) {
        const data = await aeoRes.json();
        if (data.success) {
          setAeoHighlights({
            summary: data.summary,
            recentBenchmarks: data.recentBenchmarks,
            periodDays: data.periodDays
          });
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setWorkspaceLoading(false);
      setRecommendationsLoading(false);
      setMarketLoading(false);
      setAeoLoading(false);
    }
  };

  // Generate workspace items from metrics (fallback)
  const generateWorkspaceFromMetrics = () => {
    if (!metrics?.topCategories) return;

    const workItems: CategoryWorkItem[] = metrics.topCategories.slice(0, 6).map((cat, idx) => {
      const enrichmentPct = cat.count > 0 ? Math.round((cat.enriched / cat.count) * 100) : 0;
      let stage: CategoryWorkItem['stage'] = 'early';

      if (enrichmentPct >= 80) stage = 'content_ready';
      else if (enrichmentPct >= 50) stage = 'enriched';

      return {
        id: `cat-${idx}`,
        name: cat.name.split(' > ').pop() || cat.name,
        path: cat.name,
        stage,
        metrics: {
          totalProducts: cat.count,
          enrichedProducts: cat.enriched,
          enrichmentPercentage: enrichmentPct,
          hasAEOContent: false,
          hasQuestionWheel: false,
          pendingReviews: 0
        },
        suggestedAction: enrichmentPct < 50 ? {
          label: 'Enrich',
          path: `/about-business/offering?category=${encodeURIComponent(cat.name)}`,
          priority: 'high' as const
        } : undefined
      };
    });

    setCategoryWorkItems(workItems);
  };

  // Generate recommendations from available data (fallback)
  const generateRecommendationsFromMetrics = () => {
    if (!metrics) return;

    const recs: Recommendation[] = [];

    // Low enrichment recommendation
    if (metrics.enrichmentPercentage < 50) {
      recs.push({
        id: 'low-enrichment',
        type: 'quality',
        priority: 'high',
        title: 'Boost Product Enrichment',
        description: `Only ${metrics.enrichmentPercentage}% of products are enriched. AI enrichment extracts valuable attributes.`,
        reason: 'Enriched products perform 3x better in AI search results',
        action: {
          label: 'Start Enriching',
          path: '/about-business/offering?tab=products'
        },
        metrics: {
          impact: 'High',
          effort: '~5 min per batch'
        }
      });
    }

    // Pending reviews
    if (metrics.pendingReviews > 0) {
      recs.push({
        id: 'pending-reviews',
        type: 'quality',
        priority: 'medium',
        title: `${metrics.pendingReviews} Products Need Review`,
        description: 'AI flagged some products with potential data issues that need your attention.',
        reason: 'Clean data improves recommendation accuracy',
        action: {
          label: 'Review Now',
          path: '/research/enrichment-review'
        },
        metrics: {
          impact: 'Medium',
          effort: '~2 min each'
        }
      });
    }

    // No AEO content
    if (metrics.aeoPages === 0) {
      recs.push({
        id: 'no-aeo',
        type: 'content',
        priority: 'high',
        title: 'Create Your First AEO Page',
        description: 'AEO pages help your products appear in AI-powered search results.',
        reason: 'AEO content captures growing AI search traffic',
        action: {
          label: 'Create AEO',
          path: '/about-business/offering?tab=categories'
        },
        metrics: {
          impact: 'High',
          effort: '~10 min'
        }
      });
    }

    // Category content generation recommendations
    if (metrics.aeoCategories !== undefined) {
      const topCat = metrics.topCategories?.[0];

      // If we have categories but no category AEO pages
      if ((metrics.aeoCategories || 0) === 0 && topCat) {
        recs.push({
          id: 'no-category-aeo',
          type: 'content',
          priority: 'high',
          title: 'Generate Category Landing Pages',
          description: `${topCat.name} has ${topCat.count} products — create an AEO category page to capture "best ${topCat.name}" queries.`,
          reason: 'Category pages capture 15-25% of commercial search queries that product pages miss',
          action: {
            label: 'Create Category Page',
            path: '/about-business/offering?tab=categories'
          },
          metrics: {
            impact: 'High',
            effort: '~2 min per category'
          }
        });
      }

      // If we have category pages but no buying guides
      if ((metrics.aeoCategories || 0) > 0 && topCat) {
        recs.push({
          id: 'category-buying-guide',
          type: 'opportunity',
          priority: 'medium',
          title: 'Create Buying Guides & Comparisons',
          description: `Your categories have landing pages. Add buying guides and brand comparison pages to capture "how to choose" and "vs" queries.`,
          reason: 'Buying guide queries have 2x higher conversion intent than generic category searches',
          action: {
            label: 'View Categories',
            path: '/about-business/offering?tab=categories'
          },
          metrics: {
            impact: 'High',
            effort: '~3 min per page'
          }
        });
      }
    }

    // Quick win: Create article
    if (metrics.enrichedProducts > 10) {
      recs.push({
        id: 'create-article',
        type: 'quick_win',
        priority: 'low',
        title: 'Create a Buyer Persona Article',
        description: 'You have enriched products ready. Create targeted content for your buyer personas.',
        reason: 'Persona-targeted content increases engagement by 40%',
        action: {
          label: 'Write Article',
          path: '/ai-articles?focus=buyer-personas'
        },
        metrics: {
          impact: 'Medium',
          effort: '~15 min'
        }
      });
    }

    setRecommendations(recs);
  };

  // Generate recommendations when metrics load
  useEffect(() => {
    if (metrics && recommendations.length === 0) {
      generateRecommendationsFromMetrics();
    }
    if (metrics && categoryWorkItems.length === 0) {
      generateWorkspaceFromMetrics();
    }
  }, [metrics]);

  if (siteLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Site Selected</h3>
            <p className="text-muted-foreground text-center mb-4">
              Please select a site from the dropdown at the top of the page to view your dashboard.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Welcome Modal for first-time users */}
      <WelcomeModal
        open={showWelcome}
        onClose={handleWelcomeClose}
        userName={user?.name}
        siteName={selectedSite?.name || selectedSite?.url}
      />

      {/* Welcome Header with Quick Stats */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0] || 'Content Manager'}!</h1>
          <p className="text-muted-foreground mt-1">
            Here's your workspace for <span className="font-medium">{selectedSite.name || selectedSite.url}</span>
          </p>
        </div>

        {/* Quick Stats Badges */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{metrics?.totalProducts?.toLocaleString() || 0}</span>
            <span className="text-xs text-muted-foreground">products</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-950/50">
            <Sparkles className="h-4 w-4 text-purple-500" />
            <span className="text-sm font-medium">{metrics?.enrichmentPercentage || 0}%</span>
            <span className="text-xs text-muted-foreground">enriched</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-950/50">
            <FileText className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium">{metrics?.aeoPages || 0}</span>
            <span className="text-xs text-muted-foreground">AEO pages</span>
          </div>
          <Button onClick={() => fetchDashboardData(true)} variant="outline" size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Good News Widget - AI-generated positive highlights */}
      <GoodNewsWidget siteId={selectedSite._id || selectedSite.id} />

      {/* AI Recommendations - Priority Section */}
      <SmartRecommendations
        recommendations={recommendations}
        loading={recommendationsLoading}
        maxDisplay={4}
      />

      {/* My Suggestions - Track submitted feedback */}
      <div className="w-full max-w-md">
        <MySuggestionsWidget />
      </div>

      {/* Two-column layout for workspace and market intelligence */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Active Work - Category Workspace */}
        <CategoryWorkspacePanel
          categories={categoryWorkItems}
          loading={workspaceLoading}
          onRefresh={fetchDashboardData}
        />

        {/* Overall Progress Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Overall Progress
            </CardTitle>
            <CardDescription>
              Your content workflow completion status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enrichment Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">Product Enrichment</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {metrics?.enrichedProducts?.toLocaleString() || 0}/{metrics?.totalProducts?.toLocaleString() || 0}
                </span>
              </div>
              <Progress value={metrics?.enrichmentPercentage || 0} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {metrics?.enrichmentPercentage || 0}% of products have AI-extracted attributes
              </p>
            </div>

            {/* AEO Content Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">AEO Content</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {metrics?.aeoPages || 0} pages
                </span>
              </div>
              <Progress
                value={metrics?.aeoCategories && metrics.aeoCategories > 0
                  ? Math.min((metrics.aeoPages / (metrics.aeoCategories * 2)) * 100, 100)
                  : 0}
                className="h-2"
              />
              <p className="text-xs text-muted-foreground">
                Across {metrics?.aeoCategories || 0} categories
              </p>
            </div>

            {/* Review Queue */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">Review Queue</span>
                </div>
                <Badge variant={metrics?.pendingReviews && metrics.pendingReviews > 0 ? 'default' : 'secondary'}>
                  {metrics?.pendingReviews || 0} pending
                </Badge>
              </div>
              {metrics?.pendingReviews && metrics.pendingReviews > 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => navigate('/research/enrichment-review')}
                >
                  Review Flagged Products
                  <ArrowRight className="h-3 w-3 ml-2" />
                </Button>
              ) : (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  All reviews complete
                </div>
              )}
            </div>

            {/* Top Categories Quick View */}
            {metrics?.topCategories && metrics.topCategories.length > 0 && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">Top Categories by Size</h4>
                <div className="space-y-2">
                  {metrics.topCategories.slice(0, 4).map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground truncate max-w-[200px]" title={cat.name}>
                        {cat.name.split(' > ').pop()}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {cat.enriched}/{cat.count}
                        </span>
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full"
                            style={{ width: `${cat.count > 0 ? (cat.enriched / cat.count) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Market Intelligence */}
      <MarketIntelligencePanel data={marketIntelligence} loading={marketLoading} />

      {/* AEO Performance Highlights */}
      <AEOPerformanceHighlights data={aeoHighlights} loading={aeoLoading} />

      {/* Capability Toolkits - All tools organized by purpose */}
      <CapabilityToolkits
        defaultExpanded={['market-intelligence', 'content-creation']}
      />

      {/* Floating Help Panel */}
      <HelpPanel currentPage="dashboard" />
    </div>
  );
}
